/**
 * Mobile keyboard / visualViewport bridge.
 *
 * Why this exists
 * ---------------
 * Android Chrome / Samsung Internet / MIUI Browser do NOT resize the
 * `100dvh` / `100vh` layout viewport when the on-screen keyboard
 * opens. The composer therefore renders behind the keyboard, with
 * the textarea sliced in half by the keyboard's clipboard / autofill
 * suggestion bar — that's the bug visible in the screenshot.
 *
 * The fix is `window.visualViewport`. That API is the *visible*
 * portion of the viewport (i.e. excluding the keyboard) and emits
 * `resize` events when the keyboard appears / disappears. We mirror
 * its current height into a CSS custom property `--app-vh` on
 * `:root` so any layout that wants to honour the keyboard can use
 * `height: var(--app-vh)` instead of `100dvh`. iOS Safari also
 * benefits — its dynamic viewport units finally match the visible
 * region.
 *
 * Public API
 * ----------
 *   - {@link installViewportHeightTracker} — call exactly once at app
 *     boot. Wires the `visualViewport` listeners and writes
 *     `--app-vh` immediately so the first paint already has the
 *     correct height.
 *
 * Implementation notes
 * --------------------
 *   - Falls back to `window.innerHeight` when `visualViewport` is
 *     unavailable (older Android WebView, headless test runners).
 *   - Uses `requestAnimationFrame` to coalesce successive resize
 *     events so we never write the property more than once per
 *     frame even when the keyboard scrolls in.
 *   - Idempotent: calling the installer twice is a no-op the second
 *     time. Returns the cleanup thunk for tests.
 */

let installed = false;

/**
 * Install the viewport-height tracker. Returns a cleanup thunk so
 * tests can tear it down between cases. Idempotent — successive
 * calls return a no-op cleanup.
 */
export function installViewportHeightTracker(): () => void {
  if (installed) {
    return () => {
      /* already installed elsewhere */
    };
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {
      /* SSR / unit-test environments */
    };
  }
  installed = true;

  const root = document.documentElement;
  let raf = 0;

  const apply = (): void => {
    raf = 0;
    const vv = window.visualViewport;
    const h = vv?.height ?? window.innerHeight;
    // Round so we don't churn the CSS engine on sub-pixel deltas.
    const rounded = Math.round(h);
    root.style.setProperty('--app-vh', `${rounded}px`);
  };

  const schedule = (): void => {
    if (raf !== 0) return;
    raf = window.requestAnimationFrame(apply);
  };

  // Run once synchronously so the first paint is already correct.
  apply();

  const vv = window.visualViewport;
  if (vv !== null && vv !== undefined) {
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
  }
  // Window-level resize fires on rotation and when the soft
  // keyboard closes on some Android builds — covers the gaps
  // visualViewport leaves.
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);

  return () => {
    if (vv !== null && vv !== undefined) {
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
    }
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    if (raf !== 0) {
      window.cancelAnimationFrame(raf);
      raf = 0;
    }
    installed = false;
  };
}
